import { normalizeRegistryCountryRegion } from "@/utils/registries";
import { useDateUtils } from "./useDateUtils";

// Get the date conversion function from the composable
const { convertDateTimeToDate } = useDateUtils();

export interface FieldUpdateConfig<T> {
  key: keyof T;
  isString?: boolean;
  transform?: (value: any) => any;
  condition?: (value: any) => boolean;
}

export interface EnumFieldConfig<T> {
  key: keyof T;
}

/**
 * Utility class for handling AI form field population with smart update logic
 */
export class FieldUpdater<T extends Record<string, any>> {
  private data: T;
  private updatedFields: string[] = [];

  constructor(data: T) {
    this.data = data;
  }

  /**
   * Process all field types for cancellation statements
   * This is the main public API - call this instead of individual process methods
   *
   * @param extractedData - Data extracted from AI analysis
   * @param isFirstAnalysis - Whether this is the first analysis (affects update behavior)
   */
  process(extractedData: any, isFirstAnalysis: boolean): void {
    // Reset update tracking
    this.updatedFields = [];

    // Process all field types
    this.processRegularFields(
      extractedData,
      getCancellationFieldConfig<T>(),
      isFirstAnalysis
    );

    this.processEnumFields(
      extractedData,
      getCancellationEnumConfig<T>(),
      isFirstAnalysis
    );

    this.processRegistryCountryRegion(extractedData, isFirstAnalysis);
  }

  /**
   * Get count of updated fields after processing
   */
  getUpdatedFieldsCount(): number {
    return this.updatedFields.length;
  }

  /**
   * Update a single field if conditions are met
   */
  private updateField(
    key: keyof T,
    extractedValue: any,
    transformedValue: any,
    isFirstAnalysis: boolean,
    isString = true
  ): boolean {
    const currentValue = this.data[key];
    if (
      !this.shouldUpdateField(
        extractedValue,
        currentValue,
        isFirstAnalysis,
        isString
      )
    ) {
      return false;
    }

    this.data[key] = transformedValue;
    this.updatedFields.push(String(key));
    return true;
  }

  /**
   * Process regular fields using configuration
   */
  private processRegularFields(
    extractedData: any,
    fieldConfig: FieldUpdateConfig<T>[],
    isFirstAnalysis: boolean
  ): void {
    fieldConfig.forEach(({ key, isString = true, transform, condition }) => {
      const extractedValue = extractedData[key];

      // Apply condition check if specified
      if (condition && !condition(extractedValue)) {
        return;
      }

      const transformedValue = transform
        ? transform(extractedValue)
        : extractedValue;

      this.updateField(
        key,
        extractedValue,
        transformedValue,
        isFirstAnalysis,
        isString
      );
    });
  }

  /**
   * Process enum fields with direct assignment
   */
  private processEnumFields(
    extractedData: any,
    enumConfig: EnumFieldConfig<T>[],
    isFirstAnalysis: boolean
  ): void {
    enumConfig.forEach(({ key }) => {
      const extractedValue = extractedData[key];
      const currentValue = this.data[key];

      if (extractedValue && (isFirstAnalysis || !currentValue)) {
        this.data[key] = extractedValue;
        this.updatedFields.push(String(key));
      }
    });
  }

  /**
   * Process registry country/region from all known AI keys.
   */
  private processRegistryCountryRegion(
    extractedData: any,
    isFirstAnalysis: boolean
  ): void {
    const rawRegistryValue =
      extractedData.registry_country_region ??
      extractedData.country_region ??
      extractedData.country ??
      extractedData.residency;

    if (!rawRegistryValue || !String(rawRegistryValue).trim()) {
      return;
    }

    const normalizedRegistry = normalizeRegistryCountryRegion(
      String(rawRegistryValue)
    );
    if (!normalizedRegistry) {
      return;
    }

    this.updateField(
      "registry_country_region" as keyof T,
      rawRegistryValue,
      normalizedRegistry,
      isFirstAnalysis,
      true
    );
  }

  private shouldUpdateField(
    extractedValue: any,
    currentValue: any,
    isFirstAnalysis: boolean,
    isString: boolean
  ): boolean {
    if (!extractedValue || (isString && !extractedValue.trim())) {
      return false; // Never update with null/empty extracted values
    }

    if (isFirstAnalysis) {
      return true; // First analysis: overwrite defaults and empty fields
    }

    // Subsequent analysis: only update if current field is empty, null, or default
    if (currentValue === null || currentValue === undefined) {
      return true; // Update null/undefined fields
    }

    if (isString && (!currentValue || !currentValue.trim())) {
      return true; // Update empty string fields
    }

    if (!isString && (currentValue === 0 || currentValue === "")) {
      return true; // Update numeric fields that are 0 or empty string
    }

    return false; // Don't overwrite non-empty user data
  }
}

/**
 * Common field configurations for cancellation statements
 */
export const getCancellationFieldConfig = <T>(): FieldUpdateConfig<T>[] => [
  { key: "beneficiary_name" as keyof T, isString: true },
  { key: "registry_code" as keyof T, isString: true },
  {
    key: "mwh_cancelled" as keyof T,
    isString: false,
    transform: (value: any) => (value && value > 0 ? Math.floor(value) : null),
    condition: (value: any) => value && value > 0,
  },
  { key: "address" as keyof T, isString: true },
  {
    key: "period_start" as keyof T,
    isString: true,
    transform: (value: any) =>
      value && value.trim() ? convertDateTimeToDate(value) : null,
    condition: (value: any) => value && value.trim(),
  },
  {
    key: "period_end" as keyof T,
    isString: true,
    transform: (value: any) =>
      value && value.trim() ? convertDateTimeToDate(value) : null,
    condition: (value: any) => value && value.trim(),
  },
  { key: "cancellation_purpose" as keyof T, isString: true },
];

export const getCancellationEnumConfig = <T>(): EnumFieldConfig<T>[] => [
  { key: "beneficiary_type" as keyof T },
];
